import React, { useState } from "react";
import { Link } from "react-router-dom";

import {
  CreateTitleAndConfigureGameUrl,
  GdkAccessResourcesUrl,
  xboxOnboardSiteLocationPrefix,
} from "../../constants/urls";
import { useString, useStrings } from "../../localization/localizationHelpers";
import * as styles from "./DevReadinessGuide.module.css";

type GuideItemKey = "nda" | "gdk" | "ada" | "ids" | "config" | "validate" | "concept";

type GuideItem = {
  key: GuideItemKey;
  label: string;
  detail: React.ReactNode;
  show: boolean;
};

type DevReadinessGuideProps = {
  email?: string;
  ndaSignedDate?: string;
  ndaSentDate?: string;
  showIdXboxOnboardingLink: boolean;
  canSubmitIdApplication: boolean;
  isIdXboxApproved: boolean;
  onClose: () => void;
};

export const DevReadinessGuide = ({
  email,
  ndaSignedDate,
  ndaSentDate,
  showIdXboxOnboardingLink,
  canSubmitIdApplication,
  isIdXboxApproved,
  onClose,
}: DevReadinessGuideProps) => {
  const [
    guideTitle,
    guideSubtitle,
    closeLabel,
    signNdaLabel,
    yourEmailFallback,
    signToProceed,
    downloadGdkLabel,
    gdkDescription,
    gdkSubtext,
    downloadLink,
    signAdaLabel,
    adaDescription,
    adaSubtext,
    generateIdsLabel,
    generateIdsDescription,
    generateIdsSubtext,
    learnMore,
    publishServiceConfigLabel,
    publishConfigDescription,
    publishConfigSubtext,
    validateXboxServicesLabel,
    validateDescription,
    validateSubtext,
    addFirstConceptLabel,
    addFirstConceptDescription,
    idXboxOnboardingFlowLink,
  ] = useStrings(
    "devReadinessGuideTitle",
    "devGuideSubtitle",
    "devGuideCloseAriaLabel",
    "devGuideSignNda",
    "devGuideYourEmailFallback",
    "devGuideSignToProceed",
    "devGuideDownloadGdk",
    "devGuideGdkDescription",
    "devGuideGdkSubtext",
    "devGuideDownloadLink",
    "devGuideSignAda",
    "devGuideAdaDescription",
    "devGuideAdaSubtext",
    "devGuideGenerateIds",
    "devGuideGenerateIdsDescription",
    "devGuideGenerateIdsSubtext",
    "devGuideLearnMore",
    "devGuidePublishServiceConfig",
    "devGuidePublishConfigDescription",
    "devGuidePublishConfigSubtext",
    "devGuideValidateXboxServices",
    "devGuideValidateDescription",
    "devGuideValidateSubtext",
    "devGuideAddFirstConcept",
    "devGuideAddFirstConceptDescription",
    "devGuideIdXboxOnboardingFlowLink"
  );
  const ndaFormattedDate = ndaSentDate
    ? new Date(ndaSentDate).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";
  const ndaSentMessage = useString(
    "devGuideNdaSentMessage",
    email || yourEmailFallback,
    ndaFormattedDate
  );
  const [expandedItem, setExpandedItem] = useState<GuideItemKey | null>(null);

  const toggleItem = (key: GuideItemKey) => {
    setExpandedItem(expandedItem === key ? null : key);
  };

  const guideItems: GuideItem[] = [
    {
      key: "nda",
      label: signNdaLabel,
      show: !ndaSignedDate,
      detail: (
        <>
          <p className={styles.guideDetailText}>{ndaSentMessage}</p>
          <p className={styles.guideDetailSubtext}>{signToProceed}</p>
        </>
      ),
    },
    {
      key: "gdk",
      label: downloadGdkLabel,
      show: true,
      detail: (
        <>
          <p className={styles.guideDetailText}>{gdkDescription}</p>
          <p className={styles.guideDetailSubtext}>{gdkSubtext}</p>
          <div className={styles.guideDetailDivider} />
          <div className={styles.guideDetailAction}>
            <a
              href={GdkAccessResourcesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guideDetailLink}
            >
              <span className={styles.guideDetailLinkIcon}>&#x2913;</span> {downloadLink}
            </a>
            <a
              href={GdkAccessResourcesUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guideDetailExternalIcon}
            >
              &#x2197;
            </a>
          </div>
        </>
      ),
    },
    {
      key: "ada",
      label: signAdaLabel,
      show: true,
      detail: (
        <>
          <p className={styles.guideDetailText}>{adaDescription}</p>
          <p className={styles.guideDetailSubtext}>{adaSubtext}</p>
        </>
      ),
    },
    {
      key: "ids",
      label: generateIdsLabel,
      show: true,
      detail: (
        <>
          <p className={styles.guideDetailText}>{generateIdsDescription}</p>
          <p className={styles.guideDetailSubtext}>{generateIdsSubtext}</p>
          <div className={styles.guideDetailDivider} />
          <div className={styles.guideDetailAction}>
            <a
              href={CreateTitleAndConfigureGameUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guideDetailLink}
            >
              {learnMore}
            </a>
            <a
              href={CreateTitleAndConfigureGameUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guideDetailExternalIcon}
            >
              &#x2197;
            </a>
          </div>
        </>
      ),
    },
    {
      key: "config",
      label: publishServiceConfigLabel,
      show: true,
      detail: (
        <>
          <p className={styles.guideDetailText}>{publishConfigDescription}</p>
          <p className={styles.guideDetailSubtext}>{publishConfigSubtext}</p>
          <div className={styles.guideDetailDivider} />
          <div className={styles.guideDetailAction}>
            <a
              href="https://learn.microsoft.com/gaming/gdk/docs/services/fundamentals/portal-config/live-portal-summary-tab"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guideDetailLink}
            >
              {learnMore}
            </a>
            <a
              href="https://learn.microsoft.com/gaming/gdk/docs/services/fundamentals/portal-config/live-portal-summary-tab"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guideDetailExternalIcon}
            >
              &#x2197;
            </a>
          </div>
        </>
      ),
    },
    {
      key: "validate",
      label: validateXboxServicesLabel,
      show: true,
      detail: (
        <>
          <p className={styles.guideDetailText}>{validateDescription}</p>
          <p className={styles.guideDetailSubtext}>{validateSubtext}</p>
          <div className={styles.guideDetailDivider} />
          <div className={styles.guideDetailAction}>
            <a
              href="https://learn.microsoft.com/gaming/gdk/docs/services/develop/test-accounts/live-test-accounts"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guideDetailLink}
            >
              {learnMore}
            </a>
            <a
              href="https://learn.microsoft.com/gaming/gdk/docs/services/develop/test-accounts/live-test-accounts"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.guideDetailExternalIcon}
            >
              &#x2197;
            </a>
          </div>
        </>
      ),
    },
    {
      key: "concept",
      label: addFirstConceptLabel,
      show: !isIdXboxApproved,
      detail: (
        <p className={styles.guideDetailText}>
          {addFirstConceptDescription}
          {showIdXboxOnboardingLink && canSubmitIdApplication && (
            <>
              {" "}
              <Link to={`${xboxOnboardSiteLocationPrefix}/id`}>{idXboxOnboardingFlowLink}</Link>
            </>
          )}
        </p>
      ),
    },
  ];

  return (
    <div className={styles.sidebar}>
      <div className={styles.sidebarHeader}>
        <h2 className={styles.sidebarTitle}>{guideTitle}</h2>
        <button className={styles.sidebarClose} onClick={onClose} aria-label={closeLabel}>
          &times;
        </button>
      </div>
      <p className={styles.sidebarSubtitle}>{guideSubtitle}</p>

      <div className={styles.sidebarSection}>
        {guideItems.map(item => (
          <React.Fragment key={item.key}>
            {item.show && (
              <div
                className={styles.sidebarItem}
                onClick={() => toggleItem(item.key)}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    toggleItem(item.key);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <span className={styles.sidebarItemLabel}>{item.label}</span>
                <span
                  className={
                    expandedItem === item.key ? styles.sidebarItemIconOpen : styles.sidebarItemIcon
                  }
                >
                  &rsaquo;
                </span>
              </div>
            )}
            {item.show && expandedItem === item.key && (
              <div className={styles.guideDetail}>{item.detail}</div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
